import type { ApiMaintenanceEvent } from '@/api/equipmentApi'
import type { UnitUnavailability } from '@/types/storage'

/**
 * The short chip text for a table cell.
 *
 * The backend's `detail` is a sentence fragment written for an error message
 * ("out of service — compressor failed"); a status column needs two words. The
 * full detail belongs in the banner, which renders it verbatim so the refusal a
 * user hits on put-away and the explanation they read cannot disagree.
 */
export function unavailabilityLabel(u: UnitUnavailability): string | null {
  if (!u) return null
  return u.reason === 'OUT_OF_SERVICE' ? 'Out of service' : 'Under maintenance'
}

/** Open (not yet completed) maintenance for one unit, soonest due first. */
export function openEventsForUnit(
  events: ApiMaintenanceEvent[],
  equipmentId: string | null,
): ApiMaintenanceEvent[] {
  if (!equipmentId) return []
  return events
    .filter((e) => e.equipmentId === equipmentId && e.status !== 'COMPLETED')
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
}

/**
 * The soonest due date among open events, or null when there are none.
 *
 * Dates are compared as ISO strings rather than parsed: `dueDate` is a date-only
 * `YYYY-MM-DD`, and turning it into a Date would apply the viewer's timezone to
 * a value that has none — which is how a PM due today starts reading as due
 * yesterday for anyone west of UTC.
 */
export function nextDueDate(
  events: ApiMaintenanceEvent[],
  equipmentId: string | null,
): string | null {
  return openEventsForUnit(events, equipmentId)[0]?.dueDate ?? null
}
